import type { ShiftRecipeWithUserIDs } from "@models/QontactPoint";

// Lun..Dom, with the index the backend stores (0 = domingo)
const weekOrder = [1, 2, 3, 4, 5, 6, 0];
const shortNames: Record<number, string> = { 0: "Dom", 1: "Lun", 2: "Mar", 3: "Mié", 4: "Jue", 5: "Vie", 6: "Sáb" };

/** "Todos los días", "Lun a Vie", "Lun, Mié y Vie", "Lun a Mié y Sáb" */
export const formatShiftDays = (recipe: Pick<ShiftRecipeWithUserIDs, "type" | "configurationData">): string => {
    const raw = recipe.type === "weekly" ? recipe.configurationData?.weekdays : undefined;
    const days = new Set<number>(Array.isArray(raw) ? raw.map(Number) : raw ? Object.values(raw).map(Number) : []);
    if (days.size === 0 || days.size === 7) {
        return "Todos los días".localized();
    }

    // Group consecutive days in Lun..Dom order
    const groups: number[][] = [];
    for (const day of weekOrder) {
        if (!days.has(day)) continue;
        const last = groups[groups.length - 1];
        const previous = last?.[last.length - 1];
        if (last && weekOrder.indexOf(day) === weekOrder.indexOf(previous) + 1) {
            last.push(day);
        } else {
            groups.push([day]);
        }
    }

    const parts: string[] = [];
    for (const group of groups) {
        const first = shortNames[group[0]].localized();
        const last = shortNames[group[group.length - 1]].localized();
        if (group.length >= 3) {
            parts.push("%s a %s".localized(first, last));
        } else {
            group.forEach((day) => parts.push(shortNames[day].localized()));
        }
    }
    if (parts.length === 1) {
        return parts[0];
    }
    return `${parts.slice(0, -1).join(", ")} ${"y".localized()} ${parts[parts.length - 1]}`;
};

/** "9:00 a 17:00" */
export const formatShiftHours = (recipe: Pick<ShiftRecipeWithUserIDs, "hourFrom" | "hourTo">): string =>
    "%s a %s".localized(recipe.hourFrom, recipe.hourTo);

/** "4 personas · 2 asignadas" / "1 persona" */
export const formatShiftHeadcount = (recipe: Pick<ShiftRecipeWithUserIDs, "expected" | "userIDs">): string => {
    const expected = Number(recipe.expected ?? 0);
    const assigned = new Set(recipe.userIDs ?? []).size;
    const people = expected === 1 ? "1 persona".localized() : "%s personas".localized(String(expected));
    if (assigned === 0) {
        return people;
    }
    const assignedText = assigned === 1 ? "1 asignada".localized() : "%s asignadas".localized(String(assigned));
    return `${people} · ${assignedText}`;
};
